'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface DiveSite {
  name: string;
  probability: string;
}

interface SeasonalAvailability {
  season: string;
  probability: string;
}

interface SpeciesDetails {
  id: number;
  commonName: string;
  scientificName: string;
  category: string;
  image: string;
  alt: string;
  caption?: string;
  conservationStatus: string;
  description: string;
  physicalDescription: string;
  behavior: string;
  size: string;
  habitat: string;
  diverNotes?: string[];
  tags?: string[];
  bestDiveSites: DiveSite[];
  seasonalAvailability: SeasonalAvailability[];
  interestingFacts: string[];
  conservationInfo: string;
}

interface SpeciesModalProps {
  species: SpeciesDetails | null;
  onClose: () => void;
}

const SpeciesModal = ({ species, onClose }: SpeciesModalProps) => {
  useEffect(() => {
    if (species) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [species]);

  if (!species) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'least concern':
        return 'bg-success/10 text-success border-success';
      case 'near threatened':
        return 'bg-warning/10 text-warning border-warning';
      case 'vulnerable':
        return 'bg-error/10 text-error border-error';
      case 'endangered':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted';
    }
  };

  const getProbabilityIcon = (probability: string) => {
    switch (probability.toLowerCase()) {
      case 'very high':
        return { icon: 'StarIcon', color: 'text-success' };
      case 'high':
        return { icon: 'StarIcon', color: 'text-primary' };
      case 'moderate':
        return { icon: 'StarIcon', color: 'text-warning' };
      default:
        return { icon: 'StarIcon', color: 'text-muted-foreground' };
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-headline font-bold text-foreground">
              {species.commonName}
            </h2>
            <p className="text-sm text-muted-foreground italic">{species.scientificName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="relative h-64 md:h-96 rounded-xl overflow-hidden">
            <AppImage
              src={species.image}
              alt={species.alt}
              title={`${species.commonName} – Cyprus Marine Life`}
              className="w-full h-full object-cover"
            />
            {species.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-4 py-2">
                <p className="text-xs text-white/80 italic">{species.caption}</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className={`px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(species.conservationStatus)}`}>
              {species.conservationStatus}
            </div>
            <div className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/30 text-foreground">
              {species.category}
            </div>
            {species.tags && species.tags.map((tag, index) => (
              <div
                key={index}
                style={{ backgroundColor: 'rgba(111,211,224,0.1)', color: '#6FD3E0', border: '1px solid rgba(111,211,224,0.2)' }}
                className="px-3 py-1 rounded-full text-xs font-medium"
              >
                {tag}
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-foreground mb-2">
              Description
            </h3>
            <p className="text-foreground leading-relaxed">{species.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                <Icon name="EyeIcon" size={20} className="text-primary" />
                <span>Physical Description</span>
              </h3>
              <p className="text-foreground leading-relaxed">{species.physicalDescription}</p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="ArrowsPointingOutIcon" size={16} className="text-primary" />
                  <span className="text-muted-foreground">Size:</span>
                  <span className="text-foreground font-medium">{species.size}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="MapPinIcon" size={16} className="text-primary" />
                  <span className="text-muted-foreground">Habitat:</span>
                  <span className="text-foreground font-medium">{species.habitat}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                <Icon name="SparklesIcon" size={20} className="text-primary" />
                <span>Behaviour</span>
              </h3>
              <p className="text-foreground leading-relaxed">{species.behavior}</p>
            </div>
          </div>

          {species.diverNotes && species.diverNotes.length > 0 && (
            <div style={{ backgroundColor: 'rgba(111,211,224,0.06)', border: '1px solid rgba(111,211,224,0.15)' }} className="rounded-xl p-5">
              <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                <Icon name="InformationCircleIcon" size={20} className="text-primary" />
                <span>Diver Notes</span>
              </h3>
              <ul className="space-y-2">
                {species.diverNotes.map((note, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="ChevronRightIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground text-sm leading-relaxed">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="MapIcon" size={20} className="text-primary" />
              <span>Where to See in Cyprus</span>
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {species.bestDiveSites.map((site, index) => {
                const probIcon = getProbabilityIcon(site.probability);
                return (
                  <div key={index} className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <span className="text-foreground font-medium">{site.name}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name={probIcon.icon as any} size={16} className={probIcon.color} />
                      <span className={`text-xs font-medium ${probIcon.color}`}>
                        {site.probability}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="CalendarIcon" size={20} className="text-primary" />
              <span>Seasonal Availability</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {species.seasonalAvailability.map((season, index) => {
                const probIcon = getProbabilityIcon(season.probability);
                return (
                  <div key={index} className="bg-muted/30 rounded-lg p-3 text-center">
                    <div className="text-foreground font-medium mb-1">{season.season}</div>
                    <div className="flex items-center justify-center space-x-1">
                      <Icon name={probIcon.icon as any} size={14} className={probIcon.color} />
                      <span className={`text-xs ${probIcon.color}`}>{season.probability}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="LightBulbIcon" size={20} className="text-primary" />
              <span>Interesting Facts</span>
            </h3>
            <ul className="space-y-2">
              {species.interestingFacts.map((fact, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground leading-relaxed">{fact}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-primary/10 rounded-xl p-5 border-l-4 border-primary">
            <h3 className="text-lg font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
              <span>Conservation Information</span>
            </h3>
            <p className="text-foreground leading-relaxed">{species.conservationInfo}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeciesModal;